import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/utils/api";
import { MANUAL_MOVEMENT_TYPES } from "@/lib/permissions";
import type { InventoryItem } from "./InventoryList";

type Props = {
  item: InventoryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
};

export function StockMovementDialog({ item, open, onOpenChange, onSaved }: Props) {
  const [type, setType] = useState<string>("restock");
  const [quantity, setQuantity] = useState("1");
  const [countedQuantity, setCountedQuantity] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setType("restock");
      setQuantity("1");
      setCountedQuantity(item ? String(item.inventory) : "");
      setNote("");
    }
  }, [open, item]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const payload: Record<string, unknown> = {
        productId: item.productId,
        variantId: item.variantId,
        type,
        note: note.trim() || undefined,
      };
      if (type === "count_correction") {
        payload.countedQuantity = countedQuantity;
      } else {
        payload.quantity = quantity;
      }
      const { data } = await api.post("/api/inventory/movements", payload);
      return data;
    },
    onSuccess: () => {
      toast.success("Inventory update recorded");
      onSaved();
      onOpenChange(false);
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Could not update inventory");
    },
  });

  const selected = MANUAL_MOVEMENT_TYPES.find((option) => option.id === type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record stock update</DialogTitle>
          <DialogDescription>
            {item
              ? `${item.name}${item.variantLabel ? ` · ${item.variantLabel}` : ""} · ${item.inventory} on hand`
              : "Choose a reason and quantity. This creates an inventory record."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="movement-type">Reason</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="movement-type" className="w-full rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_MOVEMENT_TYPES.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selected ? (
              <p className="text-xs text-muted-foreground">{selected.description}</p>
            ) : null}
          </div>

          {type === "count_correction" ? (
            <div className="space-y-2">
              <Label htmlFor="counted-qty">Counted on-hand quantity</Label>
              <Input
                id="counted-qty"
                inputMode="numeric"
                value={countedQuantity}
                onChange={(event) => setCountedQuantity(event.target.value.replace(/[^\d]/g, ""))}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="movement-qty">Quantity</Label>
              <Input
                id="movement-qty"
                inputMode="numeric"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value.replace(/[^\d]/g, ""))}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="movement-note">Note (optional)</Label>
            <Textarea
              id="movement-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Supplier delivery, damaged carton, etc."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !item}
            className="rounded-full"
          >
            {mutation.isPending ? "Saving..." : "Record update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
